import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle, AlertCircle, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ManualConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ManualConfirmationModal = ({ open, onOpenChange }: ManualConfirmationModalProps) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const { toast } = useToast();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleConfirm = async () => {
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter an email address' });
      return;
    }

    if (!validateEmail(email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const { data, error } = await supabase.functions.invoke('manual-confirm-user', {
        body: { email: email.trim() }
      });

      if (error) {
        throw error;
      }

      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        toast({
          title: "Success",
          description: `User ${email} has been confirmed and can now log in.`,
        });
        
        // Reset form after short delay
        setTimeout(() => {
          setEmail("");
          setMessage(null);
          onOpenChange(false);
        }, 2000);
      } else if (data.alreadyConfirmed) {
        setMessage({ type: 'info', text: data.message });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to confirm user' });
      }
    } catch (error: any) {
      console.error('Error confirming user:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'An unexpected error occurred while confirming the user' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setMessage(null);
    onOpenChange(false);
  };

  const getAlertIcon = () => {
    switch (message?.type) {
      case 'success':
        return <CheckCircle className="h-4 w-4" />;
      case 'error':
        return <AlertCircle className="h-4 w-4" />;
      case 'info':
        return <Mail className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Manual User Confirmation
          </DialogTitle>
          <DialogDescription>
            Enter the customer's email address to manually confirm their account. This will allow them to log in without email verification.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Customer Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="customer@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {message && (
            <Alert className={`${
              message.type === 'success' ? 'border-green-200 bg-green-50' :
              message.type === 'error' ? 'border-red-200 bg-red-50' :
              'border-blue-200 bg-blue-50'
            }`}>
              {getAlertIcon()}
              <AlertDescription className={`${
                message.type === 'success' ? 'text-green-800' :
                message.type === 'error' ? 'text-red-800' :
                'text-blue-800'
              }`}>
                {message.text}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !email.trim()}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualConfirmationModal;